import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from './types';
import { PunchupCheckbox } from 'features/common/components/Punchup/primitives/forms/fields/PunchupCheckbox';
import styles from './PunchupColumnPicker.module.css';

interface PunchupColumnPickerProps {
	allDefs: ColumnDef[];
	visibleKeys: Set<string>;
	onToggle: (key: string) => void;
	/** Override the button label. Defaults to "Columns". */
	label?: string;
	/** Override the dropdown header text. Defaults to "Show columns". */
	dropdownLabel?: string;
	/**
	 * Override the trigger icon (defaults to a column-grid SVG). Use this when a
	 * picker semantically represents something other than table-column visibility
	 * — e.g. the "Custom Questions" picker on the Attendee List page passes a
	 * help / question-mark icon to differentiate it from the static "Columns"
	 * picker that sits next to it.
	 */
	icon?: ReactNode;
	/**
	 * When true, all columns may be deselected (no last-item lock).
	 * Use for optional column groups (e.g. Custom Fields) where empty selection is valid.
	 */
	allowEmpty?: boolean;
}

/* Dropdown geometry used by the viewport-edge clamp in the positioning effect. */
const DROPDOWN_WIDTH_PX = 200; // Must match .dropdown min-width in PunchupColumnPicker.module.css
const ROW_HEIGHT_PX = 36;
const DROPDOWN_CHROME_PX = 52; // label row height + top/bottom padding + label margin
const TRIGGER_GAP_PX = 4; // vertical gap between the trigger and the panel
const VIEWPORT_GUTTER_PX = 8; // minimum gap kept between the panel and the viewport edge

const DefaultIcon: FC = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
		<rect x="1" y="3" width="14" height="1.5" rx="0.75" fill="currentColor" />
		<rect x="1" y="7.25" width="14" height="1.5" rx="0.75" fill="currentColor" />
		<rect x="1" y="11.5" width="14" height="1.5" rx="0.75" fill="currentColor" />
		<circle cx="5" cy="3.75" r="2" fill="var(--punchup-color-bg)" stroke="currentColor" strokeWidth="1.5" />
		<circle cx="11" cy="8" r="2" fill="var(--punchup-color-bg)" stroke="currentColor" strokeWidth="1.5" />
		<circle cx="6" cy="12.25" r="2" fill="var(--punchup-color-bg)" stroke="currentColor" strokeWidth="1.5" />
	</svg>
);

export const PunchupColumnPicker: FC<PunchupColumnPickerProps> = ({
	allDefs,
	visibleKeys,
	onToggle,
	label,
	dropdownLabel,
	icon,
	allowEmpty,
}) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
	const buttonRef = useRef<HTMLButtonElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		const handleOutsideClick = (e: MouseEvent) => {
			const target = e.target as Node;
			if (
				buttonRef.current &&
				!buttonRef.current.contains(target) &&
				dropdownRef.current &&
				!dropdownRef.current.contains(target)
			) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handleOutsideClick);
		return () => document.removeEventListener('mousedown', handleOutsideClick);
	}, [open]);

	useEffect(() => {
		if (!open || !buttonRef.current) return;
		const rect = buttonRef.current.getBoundingClientRect();
		const dropdownHeight = allDefs.length * ROW_HEIGHT_PX + DROPDOWN_CHROME_PX;

		let top = rect.bottom + TRIGGER_GAP_PX;
		let left = rect.right - DROPDOWN_WIDTH_PX;

		if (left + DROPDOWN_WIDTH_PX > window.innerWidth) {
			left = window.innerWidth - DROPDOWN_WIDTH_PX - VIEWPORT_GUTTER_PX;
		}
		if (left < VIEWPORT_GUTTER_PX) {
			left = VIEWPORT_GUTTER_PX;
		}
		if (top + dropdownHeight > window.innerHeight) {
			top = rect.top - dropdownHeight - TRIGGER_GAP_PX;
		}
		if (top < VIEWPORT_GUTTER_PX) {
			top = VIEWPORT_GUTTER_PX;
		}

		setDropdownPos({ top, left });
	}, [open, allDefs.length]);

	const visibleCount = visibleKeys.size;
	const triggerLabel = label ?? t('column-picker-button', 'Columns');

	const dropdown = (
		<div
			ref={dropdownRef}
			className={styles.dropdown}
			style={{ top: dropdownPos.top, left: dropdownPos.left }}
			role="listbox"
			aria-multiselectable="true"
		>
			<p className={styles.dropdownLabel}>{dropdownLabel ?? t('column-picker-label', 'Show columns')}</p>
			{allDefs.map((col) => {
				const checked = visibleKeys.has(col.key);
				const isLastVisible = !allowEmpty && checked && visibleKeys.size === 1;
				return (
					<div key={col.key} className={`${styles.option} ${isLastVisible ? styles.optionDisabled : ''}`}>
						<PunchupCheckbox
							label={t(col.labelKey)}
							checked={checked}
							disabled={isLastVisible}
							onChange={() => onToggle(col.key)}
						/>
					</div>
				);
			})}
		</div>
	);

	return (
		<div className={styles.container}>
			<button
				ref={buttonRef}
				type="button"
				className={styles.trigger}
				onClick={() => setOpen((v) => !v)}
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-label={triggerLabel}
			>
				{icon ?? <DefaultIcon />}
				<span className={styles.label}>{triggerLabel}</span>
				<span className={styles.badgeVisible}>{visibleCount}</span>
			</button>

			{open && ReactDOM.createPortal(dropdown, document.body)}
		</div>
	);
};
